<section class="dashboard-page" [class.compact]="density === 'compact'">
    <!-- 1. Oracle Fusion–Style Top Tiled Springboard -->
    <header class="springboard-hero">
        <div class="springboard-topline">
            <div>
                <h1 class="springboard-greeting">{{ greeting }}, {{ customerName }}</h1>
                <p class="springboard-subline">Welcome to your LogiLink workspace</p>
            </div>

            <div class="springboard-stats" *ngIf="dashboard">
                <div class="stat-pill">
                    <strong>{{ dashboard.summary.totalOpenItems }}</strong>
                    <span>{{ 'dashboard.openWork' | transloco }}</span>
                </div>
                <div class="stat-pill" [class.warn]="dashboard.summary.overdueItems > 0">
                    <strong>{{ dashboard.summary.overdueItems }}</strong>
                    <span>{{ 'dashboard.overdue' | transloco }}</span>
                </div>
                <div class="stat-pill">
                    <strong>{{ dashboard.summary.availableModuleCount }}</strong>
                    <span>{{ 'dashboard.liveModules' | transloco }}</span>
                </div>
            </div>
        </div>

        <!-- Horizontal Module Tabs -->
        <nav class="springboard-tabs-bar" role="tablist" aria-label="Springboard Modules">
            @for (tab of springboardTabs; track tab.id) {
            <button type="button"
                    role="tab"
                    class="springboard-tab-btn"
                    [class.active]="activeTabId === tab.id"
                    [attr.aria-selected]="activeTabId === tab.id"
                    (click)="selectSpringboardTab(tab.id)">
                {{ tab.label }}
            </button>
            }
        </nav>

        <!-- Active Tab Springboard Body -->
        @if (activeTab) {
        <div class="springboard-body">
            @if (activeTab.quickActions.length) {
            <aside class="springboard-qa-col">
                <h2 class="column-heading">{{ 'dashboard.quickActions' | transloco }}</h2>
                <div class="qa-list">
                    @for (qa of activeTab.quickActions; track qa.id) {
                    <a [routerLink]="qa.route" class="qa-item" (click)="onQuickActionClick(qa)">
                        <i [class]="qa.icon"></i>
                        <span>{{ qa.label }}</span>
                    </a>
                    }
                </div>
            </aside>
            }

            <div class="springboard-apps-col" [class.full-width]="!activeTab.quickActions.length">
                <div class="apps-head">
                    <h2 class="column-heading">{{ 'dashboard.apps' | transloco }}</h2>
                    @if (activeTab.subGroups.length) {
                    <div class="subgroup-pills">
                        @for (group of activeTab.subGroups; track group) {
                        <button type="button"
                                class="subgroup-pill"
                                [class.active]="activeSubGroup === group"
                                (click)="selectSubGroup(group)">
                            {{ group }}
                        </button>
                        }
                    </div>
                    }
                </div>

                <div class="apps-grid">
                    @for (tile of activeTiles; track tile.id) {
                    <a [routerLink]="tile.route"
                       class="app-tile"
                       [class.frequent]="tile.isFrequent"
                       (click)="onTileClick(tile)">
                        <div class="tile-icon-box">
                            <i [class]="tile.icon"></i>
                        </div>
                        <span class="tile-label">{{ tile.label }}</span>
                        @if (tile.isFrequent && activeTabId === 'main') {
                        <span class="frequent-dot" title="Frequently accessed"></span>
                        }
                    </a>
                    } @empty {
                    <div class="apps-empty">
                        <i class="pi pi-th-large"></i>
                        <span>No apps available in this section.</span>
                    </div>
                    }
                </div>
            </div>
        </div>
        }
    </header>

    <!-- Error State -->
    <div *ngIf="loadError" class="state-panel error-state">
        <i class="pi pi-exclamation-triangle"></i>
        <div>
            <strong>We couldn't load your dashboard</strong>
            <span>{{ loadError }}</span>
        </div>
        <button pButton type="button" label="Try again" icon="pi pi-refresh" severity="danger"
                [outlined]="true" (click)="loadDashboard()"></button>
    </div>

    <!-- Loading Skeleton -->
    <div *ngIf="loading" class="dashboard-skeleton" aria-label="Loading dashboard">
        <div *ngFor="let item of [1,2,3,4,5,6]" class="skeleton-card"></div>
    </div>

    <ng-container *ngIf="!loading && dashboard">
        <!-- 2. Oracle Fusion–Style Middle Band: Things to Finish -->
        <section class="things-to-finish-section">
            <div class="things-heading">
                <h2>{{ 'dashboard.thingsToFinish' | transloco }}</h2>
            </div>

            <div class="things-strip">
                <!-- Left Summary Tally Card -->
                <div class="things-tally-card">
                    <div class="tally-box">
                        <span class="tally-label">{{ 'dashboard.assignedToMe' | transloco }}</span>
                        <strong class="tally-number">{{ dashboard.summary.totalOpenItems }}</strong>
                    </div>
                    <div class="tally-box">
                        <span class="tally-label">{{ 'dashboard.overdue' | transloco }}</span>
                        <strong class="tally-number" [class.has-overdue]="dashboard.summary.overdueItems > 0">{{ dashboard.summary.overdueItems }}</strong>
                    </div>
                </div>

                <!-- Carousel Cards -->
                <div class="things-carousel-container">
                    <button type="button" class="things-nav-btn left" (click)="scrollThings('left')" aria-label="Scroll left">
                        <i class="pi pi-chevron-left"></i>
                    </button>

                    <div class="things-scroll-track" #thingsTrack>
                        @for (item of thingsToFinishItems.slice(0, 12); track item.id) {
                        <article class="thing-card" [class]="'severity-' + item.severity">
                            <div class="thing-top">
                                <span class="thing-age">{{ itemAgeText(item) }}</span>
                                <p-tag [value]="item.status || item.severity"
                                       [severity]="item.severity === 'danger' ? 'danger' : item.severity === 'warn' ? 'warn' : 'info'"></p-tag>
                            </div>
                            <h3 class="thing-title" [title]="item.title">{{ item.title }}</h3>
                            <p class="thing-sub">{{ item.subtitle || (moduleLabel(item.module) + ' / ' + item.type) }}</p>
                            <div class="thing-action">
                                <a pButton [routerLink]="itemRoute(item)" label="View" icon="pi pi-arrow-right"
                                   size="small" [outlined]="true" severity="secondary"></a>
                            </div>
                        </article>
                        } @empty {
                        <div class="things-empty-card">
                            <i class="pi pi-check-circle"></i>
                            <span>{{ 'dashboard.noThingsToFinish' | transloco }}</span>
                        </div>
                        }
                    </div>

                    <button type="button" class="things-nav-btn right" (click)="scrollThings('right')" aria-label="Scroll right">
                        <i class="pi pi-chevron-right"></i>
                    </button>
                </div>
            </div>

            <div class="things-toggle-wrap">
                <button type="button" class="things-toggle-btn" (click)="toggleThingsToFinishView()">
                    <span>{{ (showMoreThingsToFinish ? 'dashboard.showLess' : 'dashboard.showMore') | transloco }}</span>
                    <i class="pi" [class.pi-chevron-down]="!showMoreThingsToFinish" [class.pi-chevron-up]="showMoreThingsToFinish"></i>
                </button>
            </div>

            <!-- Expanded Operations Panel (Module Filter & Search) -->
            @if (showMoreThingsToFinish) {
            <div class="things-expanded-panel">
                <nav class="module-tabs" aria-label="Dashboard modules">
                    <button type="button" [class.active]="selectedModule === 'ALL'" (click)="selectModule('ALL')">
                        <i [class]="moduleIcon('ALL')"></i>
                        <span>All work</span>
                        <strong>{{ dashboard.summary.totalOpenItems }}</strong>
                    </button>
                    <button *ngFor="let module of enabledModules" type="button"
                            [class.active]="selectedModule === module.key" (click)="selectModule(module.key)">
                        <i [class]="moduleIcon(module.key)"></i>
                        <span>{{ module.label }}</span>
                        <strong>{{ module.itemCount || 0 }}</strong>
                    </button>
                </nav>

                <div class="feed-toolbar">
                    <div class="feed-filters">
                        <span class="search-field">
                            <i class="pi pi-search"></i>
                            <input pInputText type="search" placeholder="Search work" [(ngModel)]="searchTerm">
                        </span>
                        <p-select [(ngModel)]="selectedSeverity" [options]="severitySelectOptions" optionLabel="label" optionValue="value"
                                  placeholder="Severity" styleClass="w-36"></p-select>
                    </div>
                </div>

                <div *ngIf="filteredItems.length === 0" class="state-panel empty-state">
                    <i class="pi pi-check-circle"></i>
                    <div>
                        <strong>You're all caught up</strong>
                        <span>No pending work for this view.</span>
                    </div>
                </div>

                <div *ngIf="filteredItems.length" class="work-list">
                    <article *ngFor="let item of filteredItems" class="work-item" [class]="'severity-' + item.severity">
                        <span class="item-icon"><i [class]="moduleIcon(item.module)"></i></span>
                        <div class="item-main">
                            <div class="item-title-row">
                                <div>
                                    <span>{{ moduleLabel(item.module) }} / {{ item.type }}</span>
                                    <h3>{{ item.title }}</h3>
                                </div>
                                <p-tag [value]="item.status || item.severity"
                                       [severity]="item.severity === 'danger' ? 'danger' : item.severity === 'warn' ? 'warn' : 'info'"></p-tag>
                            </div>
                            <p *ngIf="item.subtitle">{{ item.subtitle }}</p>
                            <div class="item-meta">
                                <span *ngIf="item.dueDate"><i class="pi pi-calendar"></i>{{ item.dueDate | date:'mediumDate' }}</span>
                                <span *ngIf="item.ageHours !== undefined && item.ageHours !== null"><i class="pi pi-clock"></i>{{ item.ageHours }}h old</span>
                                <span *ngIf="item.amount !== undefined && item.amount !== null"><i class="pi pi-money-bill"></i>{{ item.amount | currency:(item.currencyCode || 'SAR'):'symbol':'1.0-2' }}</span>
                            </div>
                        </div>
                        <a pButton [routerLink]="itemRoute(item)" icon="pi pi-arrow-right" severity="secondary" [outlined]="true"></a>
                    </article>
                </div>
            </div>
            }
        </section>

        <!-- 3. Oracle Fusion–Style Bottom Band: Analytics (KPIs & Operational Intelligence) -->
        <section class="analytics-section">
            <header class="analytics-toolbar">
                <div>
                    <h2>{{ 'dashboard.analytics' | transloco }}</h2>
                    <p>Updated {{ dashboard.generatedAt | date:'shortTime' }}</p>
                </div>

                <div class="toolbar-actions">
                    <p-datePicker [(ngModel)]="selectedDateRange"
                                  selectionMode="range"
                                  [showIcon]="true"
                                  [showButtonBar]="true"
                                  dateFormat="yy-mm-dd"
                                  styleClass="date-range"
                                  (onSelect)="onDateRangeChange()"
                                  (onClear)="onDateRangeChange()">
                    </p-datePicker>
                    <button pButton type="button" icon="pi pi-refresh" severity="secondary" [outlined]="true"
                            aria-label="Refresh dashboard" (click)="loadDashboard()" [disabled]="loading"></button>
                    <button pButton type="button" icon="pi pi-sliders-h" label="Customize" severity="secondary"
                            [outlined]="true" (click)="toggleSettings()"></button>
                </div>
            </header>

            <div class="section-subheading" *ngIf="kpiWidgets.length">
                <div>
                    <span class="eyebrow">At a glance</span>
                    <h3>Key performance indicators</h3>
                </div>
                <button type="button" class="text-action" (click)="toggleSettings()">
                    <i class="pi pi-plus"></i> Add or arrange cards
                </button>
            </div>

            <!-- KPI Ribbon -->
            <section *ngIf="kpiWidgets.length" class="kpi-ribbon">
                <article *ngFor="let widget of kpiWidgets"
                         class="kpi-card"
                         [class]="'size-' + cardSize(widget) + ' accent-' + cardAccent(widget) + ' severity-' + widget.severity">
                    <div class="kpi-heading">
                        <span class="kpi-icon"><i [class]="cardIcon(widget)"></i></span>
                        <span>{{ widget.title }}</span>
                        <button type="button" class="icon-action" aria-label="Remove card"
                                (click)="removeWidget(widget.key, $event)">
                            <i class="pi pi-times"></i>
                        </button>
                    </div>
                    <strong>{{ widgetValue(widget) }}</strong>
                    <div class="kpi-footer">
                        <span class="comparison"
                              [class.favorable]="widget.payload?.comparison?.status === 'FAVORABLE'"
                              [class.unfavorable]="widget.payload?.comparison?.status === 'UNFAVORABLE'">
                            {{ comparisonText(widget) }}
                        </span>
                        <a [routerLink]="widget.route || '/dashboard'" [attr.aria-label]="'Open ' + widget.title">
                            <i class="pi pi-arrow-up-right"></i>
                        </a>
                    </div>
                </article>
            </section>

            <div class="section-subheading" *ngIf="richWidgets.length">
                <div>
                    <span class="eyebrow">Performance & trends</span>
                    <h3>Operational intelligence</h3>
                </div>
                <span class="comparison-period">Compared with the previous equal period</span>
            </div>

            <!-- Operational Intelligence Grid -->
            <section *ngIf="richWidgets.length"
                     class="card-grid"
                     cdkDropList
                     cdkDropListOrientation="mixed"
                     [cdkDropListData]="richWidgets"
                     (cdkDropListDropped)="dropWidget($event)">
                <article *ngFor="let widget of richWidgets"
                         class="metric-card rich-card"
                         [class]="'size-' + cardSize(widget) + ' accent-' + cardAccent(widget) + ' severity-' + widget.severity"
                         cdkDrag>
                    <div class="card-placeholder" *cdkDragPlaceholder></div>
                    <div class="card-drag-preview" *cdkDragPreview>
                        <i [class]="cardIcon(widget)"></i>
                        <strong>{{ widget.title }}</strong>
                    </div>

                    <div class="card-topline">
                        <span class="module-chip">
                            <i [class]="cardIcon(widget)"></i>
                            {{ moduleLabel(widget.module) }}
                        </span>
                        <div class="card-actions">
                            <button type="button" class="icon-action drag-handle" cdkDragHandle aria-label="Move card">
                                <i class="pi pi-bars"></i>
                            </button>
                            <button type="button" class="icon-action" aria-label="Remove card"
                                    (click)="removeWidget(widget.key, $event)">
                                <i class="pi pi-times"></i>
                            </button>
                        </div>
                    </div>

                    <div class="rich-card-title">
                        <div>
                            <h4>{{ widget.title }}</h4>
                            <p>{{ cardDescription(widget) }}</p>
                        </div>
                        <span class="visualization-badge">{{ visualizationLabel(visualization(widget)) }}</span>
                    </div>

                    <div class="chart-frame" *ngIf="isChartCard(widget)">
                        <p-chart [type]="chartType(widget)"
                                 [data]="chartData(widget)"
                                 [options]="chartOptions(widget)"
                                 [attr.aria-label]="widget.title + ' chart'">
                        </p-chart>
                    </div>

                    <div class="progress-list"
                         *ngIf="!isChartCard(widget) && cardPoints(widget).length">
                        <div *ngFor="let point of cardPoints(widget)" class="progress-row">
                            <div class="progress-copy">
                                <span>{{ point.label }}</span>
                                <strong>{{ formatPointValue(point) }}</strong>
                            </div>
                            <div class="progress-track">
                                <span [style.width.%]="progressWidth(point.value, cardPoints(widget))"></span>
                            </div>
                            <small *ngIf="point.detail">{{ point.detail }}</small>
                        </div>
                    </div>

                    <div class="ranked-list" *ngIf="cardRows(widget).length">
                        <a *ngFor="let row of cardRows(widget); let index = index"
                           [routerLink]="row.route || widget.route || '/dashboard'"
                           class="ranked-row">
                            <span class="rank">{{ index + 1 }}</span>
                            <div>
                                <strong>{{ row.label }}</strong>
                                <small>{{ row.detail }}</small>
                            </div>
                            <span>{{ formatPointValue(row) }}</span>
                        </a>
                    </div>

                    <div class="card-empty"
                         *ngIf="!isChartCard(widget) && !cardPoints(widget).length && !cardRows(widget).length">
                        <i class="pi pi-chart-bar"></i>
                        <span>No data for the selected period</span>
                    </div>

                    <a class="rich-card-link" [routerLink]="widget.route || '/dashboard'">
                        View details <i class="pi pi-arrow-up-right"></i>
                    </a>
                </article>
            </section>
        </section>
    </ng-container>

    <!-- Customize Drawer -->
    <div *ngIf="settingsOpen" class="settings-backdrop" (click)="toggleSettings()"></div>
    <aside *ngIf="settingsOpen" class="settings-drawer" aria-label="Customize dashboard">
        <header>
            <div>
                <span class="eyebrow">Card studio</span>
                <h2>Customize dashboard</h2>
                <p>Choose your metrics, sizes, and display density.</p>
            </div>
            <button type="button" class="icon-action" aria-label="Close settings" (click)="toggleSettings()">
                <i class="pi pi-times"></i>
            </button>
        </header>

        <div class="preset-mode" *ngIf="isAdmin">
            <div class="segmented-control">
                <button type="button" [class.active]="preferenceMode === 'USER_OVERRIDE'"
                        (click)="setPreferenceMode('USER_OVERRIDE')">My dashboard</button>
                <button type="button" [class.active]="preferenceMode === 'ROLE_DEFAULT'"
                        (click)="setPreferenceMode('ROLE_DEFAULT')">Role defaults</button>
            </div>
            <label *ngIf="preferenceMode === 'ROLE_DEFAULT'">
                <span>Publish defaults for</span>
                <p-select [(ngModel)]="selectedRoleName" [options]="roleSelectOptions" optionLabel="label" optionValue="value"
                          (ngModelChange)="loadSelectedRolePreference()" styleClass="w-full"></p-select>
            </label>
        </div>

        <div class="drawer-section catalog-section">
            <div class="drawer-label">
                <div><strong>Card catalog</strong><span>{{ allWidgets.length }} metrics available for your access</span></div>
                <button type="button" class="text-action"
                        (click)="preferenceMode === 'USER_OVERRIDE' ? resetToRoleDefault() : resetLayout()"
                        [disabled]="loadingRolePreference">
                    {{ preferenceMode === 'USER_OVERRIDE' ? 'Use role default' : 'Reset preset' }}
                </button>
            </div>

            <div class="catalog-list">
                <article *ngFor="let widget of allWidgets" class="catalog-item"
                         [class.disabled]="hiddenWidgetKeys.has(widget.key)">
                    <label class="catalog-toggle">
                        <input type="checkbox"
                               [checked]="!hiddenWidgetKeys.has(widget.key)"
                               (change)="toggleWidget(widget.key, $any($event.target).checked)">
                        <span class="toggle-track"></span>
                    </label>
                    <span class="catalog-icon" [class]="'accent-' + cardAccent(widget)">
                        <i [class]="cardIcon(widget)"></i>
                    </span>
                    <div class="catalog-copy">
                        <strong>{{ widget.title }}</strong>
                        <span>{{ moduleLabel(widget.module) }}</span>
                    </div>
                    <div class="catalog-options">
                        <p-select [ngModel]="cardSize(widget)"
                                  (ngModelChange)="setWidgetSize(widget.key, $event)"
                                  [options]="allowedCardSizes(widget)"
                                  optionLabel="label"
                                  optionValue="value"
                                  [disabled]="hiddenWidgetKeys.has(widget.key)"
                                  aria-label="Card size"
                                  styleClass="w-full text-xs"></p-select>
                        <p-select *ngIf="(widget.capabilities?.visualizations?.length || 0) > 1"
                                  [ngModel]="visualization(widget)"
                                  (ngModelChange)="setVisualization(widget, $event)"
                                  [options]="widget.capabilities?.visualizations || []"
                                  [disabled]="hiddenWidgetKeys.has(widget.key)"
                                  aria-label="Visualization"
                                  styleClass="w-full text-xs"></p-select>
                    </div>
                </article>
            </div>
        </div>

        <div class="drawer-section density-section">
            <strong>Display density</strong>
            <div class="segmented-control">
                <button type="button" [class.active]="density === 'comfortable'" (click)="density = 'comfortable'">Comfortable</button>
                <button type="button" [class.active]="density === 'compact'" (click)="density = 'compact'">Compact</button>
            </div>
        </div>

        <footer>
            <button pButton type="button" label="Cancel" severity="secondary" [outlined]="true" (click)="toggleSettings()"></button>
            <button pButton type="button" icon="pi pi-save"
                    [label]="preferenceMode === 'ROLE_DEFAULT' ? 'Publish role preset' : 'Save dashboard'"
                    (click)="savePreferences()" [loading]="savingPreference"></button>
        </footer>
    </aside>
</section>
